/**
 * Privacy-focused user information detection
 * Collects language and timezone without IP tracking
 */

/**
 * Detect user's language preferences
 * @returns {Object} Language information
 */
export const detectLanguage = () => {
  try {
    const primaryLanguage = navigator.language || 'en'
    const allLanguages = navigator.languages || [primaryLanguage]
    
    // Extract language code (e.g., 'en' from 'en-US')
    const primaryLangCode = primaryLanguage.split('-')[0]
    
    // Get language name
    const languageName = new Intl.DisplayNames([primaryLanguage], { type: 'language' }).of(primaryLangCode)
    
    return {
      primary: primaryLanguage,
      primaryCode: primaryLangCode,
      all: allLanguages,
      name: languageName || primaryLangCode,
      detected: true
    }
  } catch (error) {
    console.warn('Language detection failed:', error)
    return {
      primary: 'en',
      primaryCode: 'en',
      all: ['en'],
      name: 'English',
      detected: false
    }
  }
}

/**
 * Detect user's timezone
 * @returns {Object} Timezone information
 */
export const detectTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    
    // Get timezone offset in minutes
    const now = new Date()
    const offsetMinutes = now.getTimezoneOffset()
    const offsetHours = Math.abs(offsetMinutes) / 60
    const offsetSign = offsetMinutes <= 0 ? '+' : '-'
    const offsetString = `${offsetSign}${Math.floor(offsetHours).toString().padStart(2, '0')}:${(offsetHours % 1 * 60).toString().padStart(2, '0')}`
    
    // Get timezone city/region name
    const timezoneName = timezone.split('/').pop().replace(/_/g, ' ')
    
    return {
      id: timezone,
      name: timezoneName,
      offset: offsetString,
      offsetMinutes: offsetMinutes,
      detected: true
    }
  } catch (error) {
    console.warn('Timezone detection failed:', error)
    return {
      id: 'UTC',
      name: 'UTC',
      offset: '+00:00',
      offsetMinutes: 0,
      detected: false
    }
  }
}

/**
 * Get comprehensive user information
 * @returns {Object} Complete user info object
 */
export const getUserInfo = () => {
  const language = detectLanguage()
  const timezone = detectTimezone()
  
  return {
    language,
    timezone,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    platform: navigator.platform
  }
}

/**
 * Format user info for server storage
 * @returns {Object} Simplified user info for database
 */
export const formatUserInfoForStorage = () => {
  const info = getUserInfo()
  
  return {
    language_primary: info.language.primary,
    language_code: info.language.primaryCode,
    language_name: info.language.name,
    timezone_id: info.timezone.id,
    timezone_name: info.timezone.name,
    timezone_offset: info.timezone.offset,
    detected_at: info.timestamp
  }
}
